/**
 * Login do NOC (S3.2 — login/sessão multi-tenant).
 *
 * Email + senha → POST /api/auth/login → sessão persistida (Bearer).
 * Inclui uma seção opcional "Acesso de bancada" para configurar a API URL e o
 * ADMIN_TOKEN (fallback de bancada, sem usuário) — preserva o fluxo atual do
 * NOC sem quebrar nada.
 */
import { useState } from "react";
import { login as apiLogin } from "../lib/api.js";
import { enableRecovery } from "../lib/auth.js";
import { loadConfig, saveConfig } from "../lib/config.js";

export default function Login({ onLoggedIn, onNavigateSignup }) {
  const cfg = loadConfig();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [apiUrl, setApiUrl] = useState(cfg.apiUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [benchOpen, setBenchOpen] = useState(false);
  const [adminToken, setAdminToken] = useState(cfg.adminToken || "");

  async function handleLogin(e) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError("Informe email e senha.");
      return;
    }
    setBusy(true);
    try {
      saveConfig({ apiUrl }); // garante a URL atual antes do login
      const session = await apiLogin(email.trim(), password);
      onLoggedIn?.(session);
    } catch (err) {
      setError(err?.message || "Falha no login.");
    } finally {
      setBusy(false);
    }
  }

  function handleBench(e) {
    e.preventDefault();
    setError(null);
    if (!adminToken.trim()) {
      setError("Informe o ADMIN_TOKEN para o acesso de recuperação.");
      return;
    }
    saveConfig({ apiUrl, adminToken: adminToken.trim() });
    // SAAS-AUTH-RECOVERY-MODE-1 — ativa EXPLICITAMENTE o modo de recuperação.
    // Sem essa ação, um ADMIN_TOKEN no localStorage não autentica sozinho.
    enableRecovery();
    onLoggedIn?.(null); // sem sessão de usuário; api.js usa X-Admin-Token (recovery)
  }

  return (
    <div className="ipwd-shell min-h-screen flex items-center justify-center
                    bg-noc-bg px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center
                          rounded-2xl border border-noc-border bg-noc-panel shadow-glow">
            <svg width="30" height="30" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="5" fill="none"
                      stroke="#54E0D4" strokeWidth="2" />
              <circle cx="16" cy="16" r="2" fill="#54E0D4" />
              <path d="M3 16 H8 M24 16 H29 M16 3 V8 M16 24 V29"
                    stroke="#54E0D4" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-noc-text">
            IP WATCHDOG
          </h1>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.22em]
                        text-noc-accent">
            OPERATIONS CLOUD · ACESSO SEGURO
          </p>
        </div>

        <form
          onSubmit={handleLogin}
          className="glass-card p-6 space-y-5"
        >
          <div className="space-y-1">
            <label className="label">Email</label>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-11 px-4 rounded-xl bg-noc-bg/60
                         border border-noc-border text-noc-text
                         focus:border-noc-accent/60 outline-none"
              placeholder="voce@empresa.com"
            />
          </div>

          <div className="space-y-1">
            <label className="label">Senha</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-4 rounded-xl bg-noc-bg/60
                         border border-noc-border text-noc-text
                         focus:border-noc-accent/60 outline-none"
              placeholder="••••••••"
            />
          </div>

          <div className="space-y-1">
            <label className="label">API URL (backend)</label>
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              className="w-full h-11 px-4 rounded-xl bg-noc-bg/60
                         border border-noc-border text-noc-text
                         focus:border-noc-accent/60 outline-none font-mono text-sm"
              placeholder="http://45.168.47.110:8000"
              spellCheck={false}
            />
            <p className="text-[11px] text-noc-muted mt-1">
              Backend FastAPI. Padrão deste ambiente: http://45.168.47.110:8000
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-noc-err
                            bg-noc-err/10 border border-noc-err/30
                            rounded-lg px-3 py-2">
              <span className="led-err" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full h-11"
          >
            {busy ? "Entrando…" : "Entrar"}
          </button>

          {/* SAAS-PORTAL — cadastro público de empresa. */}
          {onNavigateSignup && (
            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => onNavigateSignup()}
                className="text-xs text-noc-accent hover:text-noc-text underline"
              >
                Não tem conta? Cadastrar minha empresa
              </button>
            </div>
          )}
        </form>

        {/* SAAS-AUTH-RETIRE-ADMIN-TOKEN — acesso de RECUPERAÇÃO / plataforma via
            X-Admin-Token (sem usuário). Não é o fluxo normal: o login Bearer
            (email+senha) é o caminho padrão e ativa o RBAC por tenant. Mantido
            só como escape hatch explícito (bancada / recuperação de plataforma). */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setBenchOpen((v) => !v)}
            className="text-xs text-noc-muted hover:text-noc-text underline"
          >
            {benchOpen
              ? "Ocultar acesso de recuperação"
              : "Acesso de recuperação / plataforma (ADMIN_TOKEN)"}
          </button>
        </div>

        {benchOpen && (
          <form
            onSubmit={handleBench}
            className="mt-3 glass-card p-6 space-y-4"
          >
            <p className="text-xs text-noc-muted">
              Use apenas para recuperação de plataforma ou bancada. O fluxo normal
              do NOC é o login por usuário (Bearer), que aplica as permissões e o
              isolamento por tenant.
            </p>
            <div className="space-y-1">
              <label className="label">API URL</label>
              <input
                type="text"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-noc-bg/60
                           border border-noc-border text-noc-text
                           focus:border-noc-accent/60 outline-none font-mono text-sm"
                placeholder="http://localhost:8000"
              />
            </div>
            <div className="space-y-1">
              <label className="label">ADMIN_TOKEN</label>
              <input
                type="password"
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
                className="w-full h-11 px-4 rounded-xl bg-noc-bg/60
                           border border-noc-border text-noc-text
                           focus:border-noc-accent/60 outline-none font-mono text-sm"
                placeholder="token de bancada"
              />
            </div>
            <button
              type="submit"
              className="btn-ghost w-full h-11"
            >
              Entrar com ADMIN_TOKEN
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
